"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navLinks, siteConfig } from "@/lib/site";
import TrackedLink from "@/components/TrackedLink";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 px-4 transition-all duration-300 sm:px-6 ${
        scrolled ? "py-3" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className={`nav-shell mx-auto max-w-7xl rounded-[28px] px-4 sm:px-5 ${scrolled ? "py-3" : "py-4"}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 shrink-0 items-center gap-3 sm:gap-4">
            <Link
              href="/"
              className="group flex min-w-0 items-center"
              aria-label={`${siteConfig.name} home`}
              data-testid="link-home"
            >
              <span className="logo-shell flex items-center rounded-[18px] px-3 py-2.5 sm:px-4 sm:py-3">
                <Image
                  src={siteConfig.logo.lockup}
                  alt={`${siteConfig.name} logo`}
                  width={1000}
                  height={278}
                  priority
                  unoptimized
                  className="h-[42px] w-auto max-w-[220px] transition-opacity group-hover:opacity-90 sm:h-[48px] sm:max-w-[240px] xl:max-w-[260px]"
                />
              </span>
            </Link>

            <div className="glow-divider hidden h-11 2xl:block" aria-hidden="true" />

            <div className="hidden max-w-[215px] shrink-0 2xl:block 2xl:max-w-[240px]">
              <p className="font-mono text-[10px] font-semibold uppercase text-sky-300">Modern websites + systems</p>
              <p className="mt-1 text-sm leading-5 text-slate-400">Make the visible and invisible work together.</p>
            </div>
          </div>

          <nav className="ml-4 hidden flex-1 items-center justify-end gap-1 lg:flex xl:gap-1.5" aria-label="Primary">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className="whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white xl:px-3 xl:text-sm"
                data-testid={`link-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
              </Link>
            ))}
            <TrackedLink
              href="/contact?interest=Website%20project"
              eventName="cta_click"
              eventProperties={{ location: "desktop_nav", offer: "website_project", label: "Discuss a Project" }}
              className="whitespace-nowrap rounded-full bg-accent-blue px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent-hover xl:px-4 xl:text-sm"
              data-testid="nav-cta"
            >
              Discuss a Project
            </TrackedLink>
          </nav>

          <button
            type="button"
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen ? (
          <motion.div
            id="mobile-navigation"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="nav-shell mx-auto mt-3 flex max-w-7xl flex-col gap-4 rounded-[24px] px-6 py-5 shadow-xl backdrop-blur-xl lg:hidden"
          >
            <div>
              <p className="font-mono text-[10px] font-semibold uppercase text-sky-300">Modern websites + systems</p>
              <p className="mt-1 text-sm leading-6 text-slate-400">Make the visible and invisible work together.</p>
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className="rounded-[14px] border border-white/10 bg-white/[0.03] px-4 py-3 text-base font-medium text-slate-300 transition-colors hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`link-mobile-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
              </Link>
            ))}
            <TrackedLink
              href="/contact?interest=Website%20project"
              eventName="cta_click"
              eventProperties={{ location: "mobile_nav", offer: "website_project", label: "Discuss a Project" }}
              className="mt-2 rounded-full bg-accent-blue px-5 py-3 text-center text-base font-semibold text-white transition-colors hover:bg-accent-hover"
              onClick={() => setMobileMenuOpen(false)}
            >
              Discuss a Project
            </TrackedLink>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
